import type { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePermission } from '../hooks/usePermission';
import { logger } from '../services/logger';
import { LoadingSpinner } from './LoadingSpinner';

interface PermissionProtectedRouteProps {
  children: ReactNode;
  requiredPermission: string;
}

export function PermissionProtectedRoute({
  children,
  requiredPermission,
}: PermissionProtectedRouteProps) {
  const { isAuthenticated, isLoading, user } = useAuth();
  const hasPermission = usePermission(requiredPermission);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" label="Authenticating..." />
      </div>
    );
  }

  if (!isAuthenticated) {
    logger.logAuth('Unauthorized access attempt - not authenticated');
    return <Navigate to="/login" replace />;
  }

  if (!hasPermission) {
    logger.logAuth('Unauthorized access attempt - missing permission', {
      userId: user?.userId,
      requiredPermission,
    });

    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="max-w-md w-full bg-white dark:bg-gray-800 shadow-lg rounded-lg p-8">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 text-center mb-2">
            Access Denied
          </h2>
          <p className="text-gray-600 dark:text-gray-400 text-center mb-6">
            You do not have permission to access this page.
          </p>
          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
            <p className="text-sm text-yellow-800 dark:text-yellow-300">
              <span className="font-semibold">Required permission:</span> {requiredPermission}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
